import { CSSProperties, useState, useMemo, useCallback, useEffect } from 'react';

import cn from 'classnames';
import * as R from 'ramda';

import { InteractiveDiv } from '../InteractiveEl';
import { getNextSortParamList, SortParam } from './sort-header-utils';
import './useSortHeader.scss';

interface SortMapEntry {
  index: number;
  ascending: boolean;
  listIndex: number;
}

interface UseSortHeaderParams {
  data: any[];
  renderer?: (args: { data: any; columnIndex: number }) => any;
  getter?: (val: any) => any;
  disableMap?: (boolean | undefined)[];
  initialSortParamList?: SortParam[];
  onUpdateSortParamList?: (params: SortParam[]) => void;
  updateOffset?: (val: number) => void;
  singleColumnSort?: boolean;
}

interface HeaderRendererArgs {
  data: any;
  columnIndex: number;
  style: CSSProperties;
  classes: string[];
}

export const useSortHeader = ({
  data,
  renderer = R.prop('data'),
  getter = R.identity,
  disableMap = [],
  initialSortParamList = [],
  onUpdateSortParamList,
  updateOffset,
  singleColumnSort = false
}: UseSortHeaderParams) => {
  const [sortParamList, setSortParamList] = useState(initialSortParamList);
  useEffect(() => {
    if (onUpdateSortParamList) {
      onUpdateSortParamList(sortParamList);
    }
  }, [onUpdateSortParamList, sortParamList]);

  const sortMap = useMemo(() => {
    if (updateOffset) {
      updateOffset(0);
    }
    const map: Record<number, SortMapEntry> = {};
    for (let i = 0; i < sortParamList.length; ++i) {
      const params = sortParamList[i];
      map[params.index] = {
        index: i,
        ascending: params.ascending,
        listIndex: i
      };
    }
    return map;
  }, [sortParamList, updateOffset]);

  const toggleSort = useCallback(
    (index: number) => {
      setSortParamList(getNextSortParamList(sortParamList, index, singleColumnSort));
    },
    [sortParamList, setSortParamList, singleColumnSort]
  );

  const sortedData = useMemo(() => {
    const comparators = sortParamList.map(({ index, ascending }) => (a: any, b: any): number => {
      const va: unknown = getter(a[index]) ?? '';
      const vb: unknown = getter(b[index]) ?? '';
      let cmp: number;
      if (typeof va === 'string' && typeof vb === 'string') {
        cmp = va.localeCompare(vb);
      } else if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = 0;
      }
      return ascending ? cmp : -cmp;
    });
    return R.sortWith(comparators, data);
  }, [data, sortParamList, getter]);

  const headerRenderer = useCallback(
    ({ data: cellData, columnIndex, style, classes }: HeaderRendererArgs) => {
      const content = (
        <span>
          {renderer({
            data: cellData,
            columnIndex
          })}
        </span>
      );

      // A column that can't sort renders as a plain div, so Space and Enter reach controls inside
      // it, e.g. a select-all checkbox.
      if (disableMap[columnIndex]) {
        return (
          <div style={style} className={cn(classes)}>
            {content}
          </div>
        );
      }

      const sortParams = sortMap[columnIndex];

      return (
        <InteractiveDiv
          style={style}
          className={cn(classes, 'use-sort-header-caret', {
            up: sortParams?.ascending === true,
            down: sortParams?.ascending === false
          })}
          onClick={() => toggleSort(columnIndex)}
        >
          {content}
        </InteractiveDiv>
      );
    },
    [renderer, sortMap, toggleSort, disableMap]
  );

  return [sortedData, headerRenderer];
};
